/**
 * Where a tooltip goes, as arithmetic.
 *
 * Separated from the DOM because this is the part that strays, and a thing that
 * strays needs to be testable without a browser. Everything here is numbers in,
 * numbers out: no elements, no measuring, no side effects.
 *
 * Two guarantees, and the second matters more than the first:
 *
 *   1. The tip is beside its anchor, on the requested side when there is room.
 *   2. **The tip is never off screen.** If no side fits, the preferred side is
 *      used and the result is clamped into view. A tip in the wrong place is a
 *      minor annoyance; a tip half outside the window is a label nobody can
 *      read, which is the whole job not done.
 */

export type TipSide = "end" | "start" | "below" | "above";

export interface Box {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface Size {
  width: number;
  height: number;
}

export interface PlaceOptions {
  /** Side to try first. */
  preferred?: TipSide;
  /** Gap between anchor and tip, in px. */
  offset?: number;
  /** Closest the tip may come to the viewport edge, in px. */
  margin?: number;
}

export interface Placement {
  left: number;
  top: number;
  side: TipSide;
  /** True when nothing fitted and the result had to be forced into view. */
  clamped: boolean;
}

/**
 * Sides to try, in order, given a preference.
 *
 * The opposite side comes second because it is the only one that keeps the tip
 * on the same axis — a tip that flips from right to left still reads as
 * belonging to the control it points at, where one that jumps underneath has
 * visibly moved. The perpendicular pair is the last resort before clamping.
 */
function order(preferred: TipSide): TipSide[] {
  const opposite: Record<TipSide, TipSide> = {
    end: "start",
    start: "end",
    below: "above",
    above: "below",
  };
  const perpendicular: Record<TipSide, TipSide[]> = {
    end: ["below", "above"],
    start: ["below", "above"],
    below: ["end", "start"],
    above: ["end", "start"],
  };
  return [preferred, opposite[preferred], ...perpendicular[preferred]];
}

/** Unclamped position for one side. */
function positionOn(side: TipSide, anchor: Box, tip: Size, offset: number): [number, number] {
  const midX = anchor.x + anchor.width / 2 - tip.width / 2;
  const midY = anchor.y + anchor.height / 2 - tip.height / 2;
  switch (side) {
    case "end":
      return [anchor.x + anchor.width + offset, midY];
    case "start":
      return [anchor.x - offset - tip.width, midY];
    case "below":
      return [midX, anchor.y + anchor.height + offset];
    case "above":
      return [midX, anchor.y - offset - tip.height];
  }
}

export function placeTip(
  anchor: Box,
  tip: Size,
  viewport: Size,
  opts: PlaceOptions = {},
): Placement {
  const offset = opts.offset ?? 10;
  const margin = opts.margin ?? 4;
  const preferred = opts.preferred ?? "end";

  const clamp = (v: number, extent: number, limit: number) =>
    Math.max(margin, Math.min(v, limit - extent - margin));

  for (const side of order(preferred)) {
    const [left, top] = positionOn(side, anchor, tip, offset);
    /*
      Only the side's OWN axis has to fit.

      A tip placed to the end is committed horizontally and free vertically, so
      it is judged on x alone and slid along y to stay in view. Testing both
      axes would reject a perfectly good side because the anchor happens to sit
      near the top of the window, and send the tip somewhere it was not asked
      to go for no benefit.
    */
    const fitsOwnAxis =
      side === "end" || side === "start"
        ? left >= margin && left + tip.width <= viewport.width - margin
        : top >= margin && top + tip.height <= viewport.height - margin;
    if (!fitsOwnAxis) continue;
    return {
      left: clamp(left, tip.width, viewport.width),
      top: clamp(top, tip.height, viewport.height),
      side,
      clamped: false,
    };
  }

  // Nothing fitted — a tip wider than the window, or an anchor squeezed against
  // an edge. Forced into view on the preferred side, because being readable in
  // the wrong place beats being correct and invisible.
  const [left, top] = positionOn(preferred, anchor, tip, offset);
  return {
    left: clamp(left, tip.width, viewport.width),
    top: clamp(top, tip.height, viewport.height),
    side: preferred,
    clamped: true,
  };
}

/**
 * Whether an anchor is somewhere a tip could sensibly point.
 *
 * A detached, hidden or zero-sized element measures as a box at the origin, and
 * a tip anchored to that lands in the top-left corner of the window with nothing
 * under it. That is the single most visible way this can go wrong, and it is one
 * comparison to rule out.
 */
export function anchorUsable(box: Box): boolean {
  return box.width > 0 && box.height > 0;
}
